import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';
import { NotificationService } from 'src/app/core/services/notification.service';
import { environment } from 'src/environments/environment';

/**
 * Download of the calculation methodology PDF.
 *
 * The request goes through HttpClient — and therefore through AuthInterceptor —
 * so an expired access token is refreshed and the call replayed. A plain
 * `<a href>` to the API would bypass the interceptor and show a raw
 * "Unauthorized access" JSON page once the 15 min token cookie has expired.
 */
@Injectable({
  providedIn: 'root',
})
export class MethodologyService {
  private http = inject(HttpClient);
  private translocoService = inject(TranslocoService);
  private notificationService = inject(NotificationService);

  readonly loading = signal(false);

  download(): void {
    if (this.loading()) {
      return;
    }
    this.loading.set(true);

    const lang = this.translocoService.getActiveLang();

    this.http
      .get(`${environment.apiUrl}/methodology`, {
        params: { lang },
        responseType: 'blob',
      })
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `methodology_${lang}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
          this.loading.set(false);
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error downloading the methodology document:', error);
          this.notificationService.error(
            this.translocoService.translate(
              error.status === 403
                ? 'methodology.packRestricted'
                : 'methodology.downloadError',
            ),
          );
          this.loading.set(false);
        },
      });
  }
}
